import {
  Badge,
  Box,
  Center,
  Flex,
  Icon,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useAllAppointmentListener } from "api/appointments";
import { format } from "date-fns";
import Link from "generics/Link";
import PageTitle from "generics/PageTitle";
import { SearchInput } from "generics/SearchInput";
import ToolbarPortal from "generics/ToolbarPortal";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import React, { useEffect, useState } from "react";
import { FaWalking, FaHome } from "react-icons/fa";

export default function ActivePatientPage() {
  const [appointments, setAppointments] = useState<any>();

  const appointmentsQuery = useAllAppointmentListener({
    status: ["active", "sample_collected"],
  });

  const [filterTerm, setFilterTerm] = useState("");
  const debouncedOnChange = useDebouncedCallback(
    (e) => setFilterTerm(e.target.value),
    400
  );

  const [pickupStatus, setPickupStatus] = useState("all");

  useEffect(() => {
    if (appointmentsQuery.data) {
      setAppointments(
        appointmentsQuery?.data?.filter(
          (i: any) =>
            i.status === "sample_collected" ||
            (i.status === "active" && i.submitSampleAtLab)
        )
      );
    }
  }, [appointmentsQuery.data]);

  function getResult(patient: any) {
    if (patient.status === "completed")
      return <Badge colorScheme="green">Published</Badge>;
    const completedTests = Object.values(patient.tests).filter(
      (test: any) => test.status === "completed"
    );
    if (completedTests.length === Object.values(patient.tests).length)
      return <Badge colorScheme="green">Published</Badge>;
    if (completedTests.length === 0)
      return <Badge colorScheme="red">Not Published</Badge>;
    return (
      <Stack direction="row">
        <Badge colorScheme="green">{completedTests.length} Published</Badge>
        <Badge colorScheme="red">
          {Object.values(patient.tests).length - completedTests.length}{" "}
          Remaining
        </Badge>
      </Stack>
    );
  }

  function renderAppointments() {
    return (
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>STATUS</Th>
            <Th>DATE</Th>
            <Th>Patient Name</Th>
            <Th>Tests</Th>
            <Th>Results</Th>
          </Tr>
        </Thead>
        <Tbody>
          {appointments?.length === 0 ? (
            <Tr>
              <Td colSpan={6}>
                <Text textAlign="center" color="gray.500">
                  No Appointments!
                </Text>
              </Td>
            </Tr>
          ) : (
            appointments?.map((appointment: any) => {
              if (pickupStatus === "home" && appointment.submitSampleAtLab)
                return null;
              if (pickupStatus === "lab" && !appointment.submitSampleAtLab)
                return null;
              return Object.values(appointment.patients)
                .filter(
                  (patient: any) =>
                    patient.status === "active" &&
                    patient.patientName
                      .toLowerCase()
                      .includes(filterTerm.toLowerCase())
                )
                .map((patient: any) => {
                  return (
                    <Tr fontSize="sm" key={patient.id}>
                      <Td flex={1} whiteSpace="nowrap">
                        <Flex>
                          <Link
                            to={`/appointments/${appointment.id}/${patient.profileId}`}
                          >
                            {patient.id || appointment.user?.readableId}
                            <Box width={1}></Box>
                            <Icon
                              color="primary.500"
                              as={
                                appointment.submitSampleAtLab ? FaWalking : FaHome
                              }
                            ></Icon>
                          </Link>

                        </Flex>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue">{patient.status}</Badge>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {format(
                          appointment.scheduledCollectionTime,
                          "d MMM, yyyy "
                        )}
                      </Td>
                      <Td whiteSpace="nowrap">{patient.patientName}</Td>
                      <Td>
                        {Object.values(patient.tests)
                          .map((test: any) => test.name)
                          .join(", ")}
                      </Td>
                      <Td>{getResult(patient)}</Td>
                    </Tr>
                  )
                }
                );
            })
          )}
        </Tbody>
      </Table>
    );
  }

  return (
    <Box>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <PageTitle flexGrow={1}>Active Patients</PageTitle>
          <Flex>
            <Select onChange={(e) => setPickupStatus(e.target.value)}>
              <option value="all">All Patients</option>
              <option value="lab">Walk In Patient</option>
              <option value="home">Sample Collected at Home</option>
            </Select>
            <SearchInput onChange={(e) => debouncedOnChange(e)} />
          </Flex>
        </Flex>
      </ToolbarPortal>
      {appointmentsQuery.isLoading || !appointmentsQuery.data ? (
        <Center w="100%" h="calc(100vh - 120px)">
          <Spinner size="lg" />
        </Center>
      ) : (
        <Box>
          {appointments?.length === 0 ? (
            <Box>
              <Center h="200px" w="100%">
                <Text fontSize="xl" color="gray.500">
                  No Patients!
                </Text>
              </Center>
            </Box>
          ) : (
            <Box px="6" pt="4">
              {renderAppointments()}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
